"use client";

import { motion, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";

export type PromptPhase = "typing" | "flying" | "fields";

interface TypewriterExamplesProps {
	examples: string[];
	/** Фаза анимации наружу: по ней родитель рисует «призрачные» поля под полем запроса. */
	onPhaseChange?: (phase: PromptPhase) => void;
}

const TYPE_MS = 55;
const FLY_MS = 620;
const HOLD_MS = 2400;
const NEXT_MS = 260;

/**
 * Примеры запросов на главной (t_1c710027): печатаются посимвольно, затем напечатанный запрос
 * «залетает» за область поля запроса к ИИ, после чего под полем вылетают три прямоугольника,
 * имитирующие сгенерированные поля (см. PromptFieldGhosts), и цикл повторяется.
 * Фаза отдаётся наружу через onPhaseChange — состояния держим в родителе, потому что текст живёт
 * над полем, а прямоугольники под ним.
 * Список примеров отдан статично в sr-only, чтобы скринридер не читал анимацию.
 */
export function TypewriterExamples({
	examples,
	onPhaseChange,
}: TypewriterExamplesProps) {
	const prefersReducedMotion = useReducedMotion() ?? false;
	const [exampleIndex, setExampleIndex] = useState(0);
	const [charCount, setCharCount] = useState(0);
	const [phase, setPhase] = useState<PromptPhase>("typing");

	const current = examples[exampleIndex] ?? "";

	// Без анимации движения нет: пример виден целиком, «призрачные» поля — статично.
	useEffect(() => {
		onPhaseChange?.(prefersReducedMotion ? "fields" : phase);
	}, [onPhaseChange, phase, prefersReducedMotion]);

	useEffect(() => {
		if (prefersReducedMotion || examples.length === 0) {
			return;
		}

		if (phase === "typing") {
			const timer = setTimeout(
				() => {
					if (charCount < current.length) {
						setCharCount((count) => count + 1);
						return;
					}
					setPhase("flying");
				},
				charCount < current.length ? TYPE_MS : 0
			);
			return () => clearTimeout(timer);
		}

		if (phase === "flying") {
			const timer = setTimeout(() => setPhase("fields"), FLY_MS);
			return () => clearTimeout(timer);
		}

		// Поля уже вылетели — держим паузу и переходим к следующему примеру
		const timer = setTimeout(() => {
			setExampleIndex((index) => (index + 1) % examples.length);
			setCharCount(0);
			setPhase("typing");
		}, HOLD_MS);
		return () => clearTimeout(timer);
	}, [charCount, current.length, examples.length, phase, prefersReducedMotion]);

	const isFlyingAway =
		!prefersReducedMotion && (phase === "flying" || phase === "fields");

	return (
		<>
			<motion.p
				animate={
					isFlyingAway
						? { filter: "blur(3px)", opacity: 0, scale: 0.88, y: 44 }
						: { filter: "blur(0px)", opacity: 1, scale: 1, y: 0 }
				}
				aria-hidden="true"
				className="relative z-0 min-h-6 font-mono text-muted-foreground text-sm"
				data-slot="demo-prompt-typing"
				initial={false}
				key={exampleIndex}
				transition={
					isFlyingAway
						? { duration: FLY_MS / 1000, ease: "easeIn" }
						: { duration: NEXT_MS / 1000, ease: "easeOut" }
				}
			>
				{prefersReducedMotion ? current : current.slice(0, charCount)}
				{prefersReducedMotion ? null : (
					<span className="ml-0.5 animate-pulse">▍</span>
				)}
			</motion.p>
			<ul className="sr-only">
				{examples.map((example) => (
					<li key={example}>{example}</li>
				))}
			</ul>
		</>
	);
}
